import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from '@wordpress/element';
import { usePricingCalculation } from './usePricingCalculation';
import { useFormFieldDetection } from './useFormFieldDetection';
import { useBookingStore } from '../store/useBookingStore';
import { numberFormat } from '../../helpers/currency';
import {
	registerBookingProductFilter,
	refreshGFProducts,
} from '../utils/gf-products';

interface GravityFormsPricingOptions {
	formId: number;
	bookingFieldId: number;
	serviceId: number;
	resourceFieldIds: number[];
}

export function useGravityFormsPricing({
	formId,
	bookingFieldId,
	serviceId,
	resourceFieldIds,
}: GravityFormsPricingOptions) {
	const [isCalculating, setIsCalculating] = useState(false);

	// Calculations can overlap (rapid occupancy changes queue several). Only
	// the last one to finish may re-enable the submit button.
	const pendingCalculations = useRef(0);

	const { calculatePrice, clearCache } = usePricingCalculation({
		formId,
		serviceId,
	});

	const { detectFormFields, getPriceInput } = useFormFieldDetection({
		formId,
		bookingFieldId,
		resourceFieldIds,
	});

	// Read from store instead of individual hooks
	const resourceIds = useBookingStore((state) => state.selectedResourceIds);
	const quantity = useBookingStore((state) => state.quantity);
	const occupancy = useBookingStore((state) => state.occupancy);
	const isInitialized = useBookingStore((state) => state.isInitialized);

	// Recalculate the total on mapped Option changes. GF versions before 3.0
	// don't recalculate for our custom inputType, so their own recalc doesn't fire.
	useEffect(() => {
		// Teach GF's product state about the booking field so its price survives
		// a recalculation triggered by any other product field on the form.
		registerBookingProductFilter();

		const form = document.getElementById(`gform_${formId}`);
		if (!form) {
			return;
		}
		const optionInputs = form.querySelectorAll(
			`.gfield_option_${formId}_${bookingFieldId} input, .gfield_option_${formId}_${bookingFieldId} select`
		);
		const handler = () => refreshGFProducts(formId);
		optionInputs.forEach((el) => el.addEventListener('change', handler));
		return () => {
			optionInputs.forEach((el) =>
				el.removeEventListener('change', handler)
			);
		};
	}, [formId, bookingFieldId]);

	const updatePricing = useCallback(
		async (bookingFieldId: number, timeValue?: string): Promise<void> => {
			// Use proper GF types from @gravitywiz/types
			if (!window.gform?.applyFilters) {
				// Fallback for older GF versions
				return;
			}

			// Parse time value
			let startTime: string | undefined;
			let endTime: string | undefined;

			if (timeValue) {
				if (timeValue.includes('|')) {
					[startTime, endTime] = timeValue.split('|');
				} else {
					startTime = timeValue;
					endTime = timeValue;
				}
			}

			// Disable submit button and show loading cursor
			pendingCalculations.current += 1;
			setIsCalculating(true);
			const submitButton = document.querySelector(
				`#gform_submit_button_${formId}`
			) as HTMLButtonElement;
			if (submitButton) {
				submitButton.disabled = true;
				submitButton.style.cursor = 'wait';
			}

			try {
				const calculatedPrice = await calculatePrice({
					resource_ids: resourceIds,
					start_time: startTime,
					end_time: endTime,
					quantity,
					occupancy: occupancy ?? undefined,
					field_id: bookingFieldId,
				});

				// Superseded or failed, so leave the displayed total alone.
				if (calculatedPrice === null) {
					return;
				}

				// Backend returns the per-unit price (calculated with quantity=1).
				// GF multiplies it by the quantity to get the product total, so
				// the price input is the only thing that needs updating here.
				const priceInput = getPriceInput(bookingFieldId);
				if (priceInput && !priceInput.dataset.updating) {
					priceInput.dataset.updating = 'true';
					priceInput.value = numberFormat(calculatedPrice);
					priceInput.dispatchEvent(
						new Event('change', { bubbles: true })
					);

					// Clean up the updating flag after a microtask
					Promise.resolve().then(() => {
						delete priceInput.dataset.updating;
					});
				}

				// Force GF to recalculate totals
				refreshGFProducts(formId);
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Failed to update booking pricing:', error);
			} finally {
				pendingCalculations.current -= 1;

				// Re-enable submit button and restore cursor
				if (pendingCalculations.current === 0) {
					setIsCalculating(false);
					const submitButton = document.querySelector(
						`#gform_submit_button_${formId}`
					) as HTMLButtonElement;
					if (submitButton) {
						submitButton.disabled = false;
						submitButton.style.cursor = '';
					}
				}
			}
		},
		[
			formId,
			calculatePrice,
			resourceIds,
			quantity,
			occupancy,
			getPriceInput,
		]
	);

	// Trigger pricing update when resourceIds, quantity, or occupancy change
	// Only run after initialization to prevent premature pricing calculations
	useLayoutEffect(() => {
		if (isInitialized && resourceIds.length > 0) {
			// Clear cache when resources, quantity, or occupancy change
			clearCache();

			// Get time field value for pricing calculation
			const { timeFieldElement } = detectFormFields();
			updatePricing(bookingFieldId, timeFieldElement?.value);
		}
	}, [
		isInitialized,
		resourceIds,
		quantity,
		occupancy,
		bookingFieldId,
		updatePricing,
		clearCache,
		detectFormFields,
	]);

	return {
		updatePricing,
		clearCache,
		isCalculating,
	};
}
